import React, {
  useRef,
  createContext,
  useState,
  useContext,
  useEffect,
} from 'react'
import Page from 'organization/Event/TemplateEditor/Page'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import {createPositions, HashMap, orderedIdsByPosition} from 'lib/list'
import {ConfigContext} from 'organization/Event/Configurable'
import {useToggleArray} from 'lib/toggle'
import {useForm} from 'react-hook-form'
import PageSettingsButton from 'organization/Marketplace/config/PageSettingsButton'
import styled from 'styled-components'
import {Typography} from '@material-ui/core'
import Container from 'Event/Marketplace/Container'
import PageSettings from 'organization/Marketplace/config/PageSettings'
import {JsonSave, REMOVE} from 'lib/JsonUpdateProvider'
import ToggleMobileModeSwitch from 'organization/Marketplace/config/ToggleMobileModeSwitch'
import MobileModeContainer from 'organization/Marketplace/config/MobileModeContainer'
import CopyIconButton from 'lib/ui/IconButton/CopyIconButton'
import EnabledSwitch from 'organization/Marketplace/config/EnabledSwitch'
import AddRowButton from 'organization/Marketplace/config/AddRowButton'
import NLConfigurableBlock from 'organization/Marketplace/config/NLConfigurableBlock'
import {
  NLPurchasePageBlock,
  NLPurchasePageTemplate,
} from 'Event/Marketplace/nl-purchase-page'
import {DragProvider} from 'organization/Marketplace/config/DragContext'
import {movedBlockEvent, MovedBlockEventDetail} from './NLConfigurableColumn'
import {DeepPartialSubstitute} from 'lib/type-utils'
import set from 'lodash/set'
import get from 'lodash/get'
import {RowBlock} from 'Event/Marketplace/Block/RowBlock'

// Create a hover context
export interface HoverContextType {
  hoveredBlockId: string | null
  setHoveredBlockId: (id: string | null) => void
}

export const HoverContext = createContext<HoverContextType>({
  hoveredBlockId: null,
  setHoveredBlockId: () => {},
})

export const useHoverContext = () => useContext(HoverContext)

interface ConfigPageProps {
  template: NLPurchasePageTemplate
  updateTemplate: JsonSave<NLPurchasePageTemplate>
  footer?: React.ReactNode
  availableBlocks: Array<NLPurchasePageBlock['type']>
  rightToolbarItems: JSX.Element
  ButtonConfig: React.FC<any>
  copyButton?: {
    value: string
    tooltip: string
  }
  enabled?: boolean
  setEnabled?: (enabled: boolean) => void
  disableEnableToggle?: boolean
  isUpsellPage?: boolean
}

export default function NLConfigPage(props: ConfigPageProps) {
  const {
    template,
    updateTemplate,
    footer,
    availableBlocks,
    rightToolbarItems,
    ButtonConfig,
    copyButton,
    enabled,
    setEnabled,
    disableEnableToggle,
  } = props

  if (!('blocks' in template)) {
    throw new Error(
      'NLConfigPage called with wrong template. It should be NLPurchasePageTemplate',
    )
  }

  const [showingConfig, toggleConfig] = useToggleArray()
  const form = useForm()
  const containerRef = useRef<HTMLDivElement>(null)
  const blocks = orderedIdsByPosition(template.blocks || {})

  const handleCopyUrl = () => {
    if (!copyButton) return
    navigator.clipboard.writeText(copyButton.value)
  }

  // Add hover state management
  const [hoveredBlockId, setHoveredBlockId] = useState<string | null>(null)

  // Listen for cross-section block move events
  useEffect(() => {
    const handleMovedBlockEvent = (event: {detail: MovedBlockEventDetail}) => {
      const {
        sourceBlockPath,
        targetBlockId,
        targetColumnPath,
        targetColumnNumber,
        targetBlockOffset,
      } = event.detail

      const row: RowBlock | undefined = get(template, targetColumnPath)
      if (!row) {
        return
      }

      const sourceBlockId = sourceBlockPath.split('.').pop()
      if (!sourceBlockId) {
        return
      }

      const sourceBlock: NLPurchasePageBlock | undefined = get(
        template,
        sourceBlockPath,
      )
      if (!sourceBlock) {
        return
      }

      if (targetBlockId && targetBlockOffset) {
        moveBlockOntoTargetBlock({
          updateTemplate,
          row,
          sourceBlockPath,
          sourceBlockId,
          sourceBlock,
          targetBlockId,
          targetColumnPath,
          targetColumnNumber,
          targetBlockOffset,
        })
      }

      const payload: DeepPartialSubstitute<
        NLPurchasePageTemplate,
        typeof REMOVE
      > = {}

      // Delete source block
      set(payload, sourceBlockPath, REMOVE)
      // add block
      set(payload, `${targetColumnPath}.blocks.${sourceBlockId}`, {
        ...sourceBlock,
        column: targetColumnNumber,
        position: 1,
      })

      updateTemplate(payload)
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener(
        movedBlockEvent,
        (handleMovedBlockEvent as unknown) as EventListener,
      )
    }

    return () => {
      if (container) {
        container.removeEventListener(
          movedBlockEvent,
          (handleMovedBlockEvent as unknown) as EventListener,
        )
      }
    }
  }, [updateTemplate, template])

  return (
    <Page
      toolbarItems={
        <>
          <EnabledSwitch
            enabled={enabled}
            setEnabled={setEnabled}
            disabled={disableEnableToggle}
          />

          <PageSettingsButton onClick={toggleConfig} />
        </>
      }
      rightToolbarItems={
        <RightToolbarItems>
          <ToggleMobileModeSwitch />
          {copyButton && (
            <StyledCopyIconButton
              aria-label="copy button"
              beforeCopyTooltip={copyButton.tooltip}
              onClick={handleCopyUrl}
            />
          )}
          {rightToolbarItems}
        </RightToolbarItems>
      }
    >
      <ConfigContext.Provider
        value={{showing: showingConfig, toggle: toggleConfig, form}}
      >
        <PageSettings template={template} updateTemplate={updateTemplate} />
        <DragProvider>
          <HoverContext.Provider value={{hoveredBlockId, setHoveredBlockId}}>
            <MobileModeContainer>
              <Container template={template} ref={containerRef}>
                {blocks.map((id) => (
                  <NLConfigurableBlock
                    key={id}
                    id={id}
                    template={template}
                    availableBlocks={availableBlocks}
                    ButtonConfig={ButtonConfig}
                    block={template.blocks[id]}
                    blockPath={`blocks.${id}`}
                    onSave={(data) => {
                      updateTemplate({
                        blocks: {
                          [id]: data,
                        },
                      })
                    }}
                    getPreviewData={(data) => ({
                      blocks: {
                        [id]: data,
                      },
                    })}
                    disableResize
                    disableAddSibling
                  />
                ))}
                <AddRowButton updateTemplate={updateTemplate} />
                {footer}
              </Container>
            </MobileModeContainer>
          </HoverContext.Provider>
        </DragProvider>
      </ConfigContext.Provider>
    </Page>
  )
}

function moveBlockOntoTargetBlock(params: {
  updateTemplate: JsonSave<NLPurchasePageTemplate>
  row: RowBlock
  sourceBlockPath: string
  sourceBlockId: string
  sourceBlock: NLPurchasePageBlock
  targetColumnPath: string
  targetBlockId: string
  targetBlockOffset: 'above' | 'below'
  targetColumnNumber: number
}) {
  const {
    updateTemplate,
    row,
    sourceBlockPath,
    sourceBlock,
    sourceBlockId,
    targetColumnPath,
    targetColumnNumber,
    targetBlockId,
    targetBlockOffset,
  } = params

  const payload: DeepPartialSubstitute<
    NLPurchasePageTemplate,
    typeof REMOVE
  > = {}

  const siblingsInColumn: HashMap<NLPurchasePageBlock> = {}
  for (const [id, block] of Object.entries(row.blocks)) {
    if (block.column === targetColumnNumber) {
      siblingsInColumn[id] = block
    }
  }

  const moved = Array.from(orderedIdsByPosition(siblingsInColumn))
  const targetBlockIndex = moved.indexOf(targetBlockId)

  const targetIndex =
    targetBlockOffset === 'above' ? targetBlockIndex : targetBlockIndex + 1

  moved.splice(targetIndex, 0, sourceBlockId)

  const updatedPositions = createPositions(moved)

  // Delete source block
  set(payload, sourceBlockPath, REMOVE)

  set(payload, `${targetColumnPath}.blocks`, updatedPositions)
  // add block
  set(payload, `${targetColumnPath}.blocks.${sourceBlockId}`, {
    ...sourceBlock,
    column: targetColumnNumber,
    position: targetIndex + 1,
  })

  updateTemplate(payload)
}

const StyledCopyIconButton = styled(CopyIconButton)`
  margin-right: 15px;
  margin-top: 3px;
`

export const ErrorText = styled(Typography)`
  text-align: center;
  background: ${(props) => props.theme.colors.error};
  color: white;
  padding: 4.5px 0px;
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 100;
  left: 232px;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    left: 0px;
  }
`

const RightToolbarItems = styled.div`
  display: flex;
  align-items: center;
`
